import { useState } from 'react'
import moment from 'moment'
import clsx from 'clsx'
import { DatePicker, Form, Input, Modal, Switch } from 'components/common'
import { CustomizedModalProps } from 'components/bookings/modals/types'

export interface BookingUsageScheduleDetail {
  approval?: boolean
  organizer?: string
  eventName?: string
  publishableDate?: moment.Moment
}

export interface BookingUsageScheduleEditModalProps
  extends Omit<CustomizedModalProps, 'onOk'> {
  usageScheduleDetails: BookingUsageScheduleDetail
  onOk: (values: BookingUsageScheduleDetail) => void
  onClose: (visible: boolean) => void
}

export const BookingUsageScheduleEditModal = ({
  visible,
  usageScheduleDetails,
  onOk,
  onClose,
  ...rest
}: BookingUsageScheduleEditModalProps): JSX.Element => {
  const [form] = Form.useForm<BookingUsageScheduleDetail>()
  const [disabledInput, setDisabledInput] = useState(true)

  const onSubmit = (values: BookingUsageScheduleDetail) => {
    onOk(values)
    onClose(false)
  }

  return (
    <Modal
      visible={visible}
      title="利用予定表を編集する"
      okText="保存する"
      cancelText="キャンセル"
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            let publishableDate
            if (values?.publishableDate) {
              publishableDate = moment(values?.publishableDate).format(
                'YYYY-MM-DD'
              )
            }
            onSubmit({
              approval: values?.approval,
              organizer: values?.organizer,
              eventName: values?.eventName,
              publishableDate: publishableDate,
            })
          })
          .catch(console.warn)
      }}
      onCancel={() => onClose(false)}
      {...rest}
    >
      <Form form={form} name="usageSchedule">
        <div className={clsx('mb-12')}>
          <Form.Item
            {...rest}
            name="approval"
            label="利用予定表への掲載"
            valuePropName="checked"
            initialValue={usageScheduleDetails?.approval || false}
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Switch
              checkedChildren="許可する"
              unCheckedChildren="許可しない"
              onChange={(checked) => setDisabledInput(!checked)}
            />
          </Form.Item>
          <Form.Item
            {...rest}
            name="eventName"
            label="掲載名称"
            initialValue={usageScheduleDetails?.eventName}
            rules={[
              {
                required: !disabledInput,
              },
              {
                type: 'string',
                min: 1,
                max: 64,
              },
            ]}
          >
            <Input autoComplete="off" disabled={disabledInput} />
          </Form.Item>
          <Form.Item
            {...rest}
            name="organizer"
            label="主催者"
            initialValue={usageScheduleDetails?.organizer}
            rules={[
              {
                required: !disabledInput,
              },
              {
                type: 'string',
                min: 1,
                max: 64,
              },
            ]}
          >
            <Input autoComplete="off" disabled={disabledInput} />
          </Form.Item>
          <Form.Item
            {...rest}
            name="publishableDate"
            label="掲載可能日"
            initialValue={
              usageScheduleDetails?.publishableDate
                ? moment(usageScheduleDetails.publishableDate)
                : null
            }
            rules={[
              {
                required: !disabledInput,
              },
            ]}
          >
            <DatePicker
              disabledDate={(current) => {
                return current && current < moment().subtract(1, 'days')
              }}
              disabled={disabledInput}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  )
}
